/**
 * ==========================================================================
 * CHARM BUILDER
 *
 * HOW TO UPDATE:
 * 1. Core functionality and utilities are at the top of the file
 * 2. Each step has its own section with related methods
 * 3. Add new methods in the relevant section
 * 4. Add new event handlers in the bindEvents method for the relevant step
 * 5. Update state properties in the constructor if needed
 *
 * STEPS SECTIONS:
 * - Step 1: Base Selection (search for STEP 1)
 * - Step 2: Charm Selection (search for STEP 2)
 * - Step 3: Review (search for STEP 3)
 * ==========================================================================
 */

class CharmBuilder {
  /**
   * ==========================================================================
   * INITIALIZATION & CORE SETUP
   * Add new state properties here if needed
   * ==========================================================================
   */
  constructor(containerId) {
    // Core Elements
    this.containerId = containerId;
    this.container = document.getElementById(containerId);
    if (!this.container) {
      console.error("Container not found:", containerId);
      return;
    }

    // UI Elements
    this.progressBar = this.container.querySelector(
      ".charm-builder__progress-bar"
    );
    this.steps = this.container.querySelectorAll(".charm-builder__step");
    this.backButton = this.container.querySelector(
      ".charm-builder__button--back"
    );
    this.nextButton = this.container.querySelector(
      ".charm-builder__button--next"
    );

    // Initialize State
    this.state = {
      currentStep: 0,
      selectedBaseProduct: null,
      selectedBaseVariant: null,
      selectedCharms: [],
      activeCategory: null,
      lastError: null,
    };

    // Create debug container and initialize
    this.createDebugContainer();
    this.init();
  }

  /**
   * ==========================================================================
   * CORE INITIALIZATION METHODS
   * Update these methods when adding new features that need initialization
   * ==========================================================================
   */
  init() {
    try {
      this.debug("Initializing Charm Builder");
      this.bindEvents();
      this.updateUI();
      this.container.__charmBuilder = this;
      this.debug("Initialization complete");
    } catch (error) {
      this.handleError("Initialization failed", error);
    }
  }

  createDebugContainer() {
    let debugContainer = this.container.querySelector(".charm-builder__debug");
    if (!debugContainer) {
      debugContainer = document.createElement("div");
      debugContainer.className = "charm-builder__debug";
      this.container.appendChild(debugContainer);
    }
    this.debugContainer = debugContainer;
  }

  /**
   * Bind click events to product cards for mobile interaction.
   * Toggles the active state to show product details.
   */
  bindProductCardClickEvents() {
    try {
      const productCards = this.container.querySelectorAll(
        ".charm-builder__product-card"
      );

      productCards.forEach((card) => {
        // Add click event listener to all product cards
        card.addEventListener("click", () => {
          if (window.innerWidth <= 768) {
            // On mobile, toggle the 'active' class to show details
            productCards.forEach((c) => c.classList.remove("active")); // Reset all cards
            card.classList.toggle("active"); // Toggle the clicked card
          }
        });
      });

      this.debug("Click events bound to all product cards successfully");
    } catch (error) {
      this.handleError("Error binding click events to product cards", error);
    }
  }

  handleCategoryTabClick() {
    const tabs = this.container.querySelectorAll(
      ".charm-builder__category-tab"
    );
    const contents = this.container.querySelectorAll(
      ".charm-builder__category-content"
    );

    // Activate the first tab and content on load
    if (tabs.length > 0 && contents.length > 0) {
      tabs[0].classList.add("active");
      contents[0].classList.add("active");
    }

    tabs.forEach((tab) => {
      tab.addEventListener("click", () => {
        // Remove active state from all tabs and contents
        tabs.forEach((t) => t.classList.remove("active"));
        contents.forEach((content) => content.classList.remove("active"));

        // Add active state to the clicked tab and its corresponding content
        tab.classList.add("active");
        const category = tab.dataset.category;
        const targetContent = this.container.querySelector(
          `.charm-builder__category-content[data-category="${category}"]`
        );
        if (targetContent) {
          targetContent.classList.add("active");
        }
      });
    });
  }

  bindCharmControls(charmElement) {
    const rotateLeftButton = charmElement.querySelector(
      '[data-action="rotate-left"]'
    );
    const rotateRightButton = charmElement.querySelector(
      '[data-action="rotate-right"]'
    );
    const removeButton = charmElement.querySelector('[data-action="remove"]');

    // Rotate Left
    rotateLeftButton?.addEventListener("click", () => {
      const currentRotation =
        parseFloat(charmElement.getAttribute("data-rotation")) || 0;
      const newRotation = currentRotation - 15;
      charmElement.setAttribute("data-rotation", newRotation);
      const x = parseFloat(charmElement.getAttribute("data-x")) || 0;
      const y = parseFloat(charmElement.getAttribute("data-y")) || 0;
      charmElement.style.transform = `translate(${x}px, ${y}px) rotate(${newRotation}deg)`;

      // Update charm state
      const productId = charmElement.dataset.productId;
      const charm = this.state.selectedCharms.find(
        (c) => c.productId === productId
      );
      if (charm) {
        charm.rotation = newRotation;
      }
    });

    // Rotate Right
    rotateRightButton?.addEventListener("click", () => {
      const currentRotation =
        parseFloat(charmElement.getAttribute("data-rotation")) || 0;
      const newRotation = currentRotation + 15;
      charmElement.setAttribute("data-rotation", newRotation);
      const x = parseFloat(charmElement.getAttribute("data-x")) || 0;
      const y = parseFloat(charmElement.getAttribute("data-y")) || 0;
      charmElement.style.transform = `translate(${x}px, ${y}px) rotate(${newRotation}deg)`;

      // Update charm state
      const productId = charmElement.dataset.productId;
      const charm = this.state.selectedCharms.find(
        (c) => c.productId === productId
      );
      if (charm) {
        charm.rotation = newRotation;
      }
    });

    // Remove Charm
    removeButton?.addEventListener("click", () => {
      charmElement.remove();
      const productId = charmElement.dataset.productId;
      this.state.selectedCharms = this.state.selectedCharms.filter(
        (c) => c.productId !== productId
      );
      this.debug("Charm removed", { productId });
    });
  }

  enableDragForCharm(charmElement) {
    const baseContainer = this.container.querySelector(
      ".charm-builder__base-container"
    );

    if (!baseContainer) {
      console.error("Base container not found.");
      return;
    }

    const uniqueId = charmElement.dataset.uniqueId; // Retrieve unique identifier

    // Use interact.js for drag functionality
    interact(charmElement).draggable({
      inertia: true,
      modifiers: [
        interact.modifiers.restrictRect({
          restriction: baseContainer, // Restrict drag within container
          endOnly: true,
        }),
      ],
      listeners: {
        start: (event) => {
          const { target } = event;

          // Ensure initial data-x and data-y are set
          if (!target.hasAttribute("data-x")) {
            target.setAttribute("data-x", 0);
          }
          if (!target.hasAttribute("data-y")) {
            target.setAttribute("data-y", 0);
          }
        },
        move: (event) => {
          const { target } = event;

          // Calculate new translate values
          const x = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx;
          const y = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy;

          // Get the current rotation
          const rotation =
            parseFloat(target.getAttribute("data-rotation")) || 0;

          // Apply updated position and rotation
          target.style.transform = `translate(${x}px, ${y}px) rotate(${rotation}deg)`;

          // Store the new position
          target.setAttribute("data-x", x);
          target.setAttribute("data-y", y);

          // Update charm's state
          const charm = this.state.selectedCharms.find(
            (c) => c.uniqueId === uniqueId
          );
          if (charm) {
            charm.x = x;
            charm.y = y;
          }
        },
        end: (event) => {
          const { target } = event;

          // Prevent sliding by resetting any remaining inertia effects
          const x = parseFloat(target.getAttribute("data-x")) || 0;
          const y = parseFloat(target.getAttribute("data-y")) || 0;

          target.style.transform = `translate(${x}px, ${y}px) rotate(${
            parseFloat(target.getAttribute("data-rotation")) || 0
          }deg)`;
        },
      },
    });
  }

  addCharmToBase(imageUrl, productId, charmTitle, charmPrice, variantId) {
    const baseContainer = this.container.querySelector(
      ".charm-builder__base-container"
    );

    if (!baseContainer) {
      this.debug("Base container not found.");
      return;
    }

    if (!imageUrl) {
      this.debug("Charm image URL is missing.");
      return;
    }

    try {
      // Generate a unique identifier for the charm
      const uniqueId = `${variantId}-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 5)}`;

      // Create the charm element
      const charmElement = document.createElement("div");
      charmElement.className = "charm-builder__charm";
      charmElement.dataset.uniqueId = uniqueId; // Assign unique ID
      charmElement.dataset.productId = productId;
      charmElement.dataset.variantId = variantId; // Store the variant ID
      charmElement.innerHTML = `
      <img src="${imageUrl}" alt="${charmTitle}">
      <div class="charm-builder__charm-controls">
        <button class="charm-builder__charm-control" data-action="rotate-left">⟲</button>
        <button class="charm-builder__charm-control" data-action="remove">✖</button>
        <button class="charm-builder__charm-control" data-action="rotate-right">⟳</button>
      </div>
    `;

      charmElement.style.position = "absolute";
      charmElement.style.top = "50%";
      charmElement.style.left = "50%";
      charmElement.style.transform = "translate(-50%, -50%)";
      charmElement.style.zIndex = 2;

      baseContainer.appendChild(charmElement);

      const charmDetails = {
        uniqueId, // Store unique identifier
        productId,
        variantId, // Store the variant ID in state
        image: imageUrl,
        title: charmTitle,
        price: charmPrice,
        x: 0,
        y: 0,
        rotation: 0,
      };

      this.state.selectedCharms.push(charmDetails);
      this.debug("Charm added successfully", charmDetails);

      this.bindCharmControls(charmElement);
      this.enableDragForCharm(charmElement);
    } catch (error) {
      this.handleError("Error adding charm to base", error);
    }
  }

  getCartData() {
    // Extract base product details
    const base = this.state.selectedBaseProduct
      ? {
          id: this.state.selectedBaseProduct.variantId,
          quantity: 1, // Assuming 1 base product per cart entry
        }
      : null;

    // Extract charm details
    const charms = this.state.selectedCharms.map((charm) => ({
      id: charm.productId, // Use product ID for Shopify's cart API
      quantity: 1, // Assuming 1 charm per selection
    }));

    // Combine base and charms, filter out null values
    return [base, ...charms].filter(Boolean);
  }

  /**
   * ==========================================================================
   * EVENT BINDING
   * Add new event listeners in the appropriate section below
   * ==========================================================================
   */
  bindEvents() {
    try {
      // Navigation Events
      this.bindNavigationEvents();

      // Step-specific Events
      this.bindStep1Events(); // Base Selection
      this.bindStep2Events(); // Charm Selection
      this.handleCategoryTabClick();
      this.bindStep3Events(); // Review

      // Global Events for Product Cards
      this.bindProductCardClickEvents();

      this.debug("Events bound successfully");
    } catch (error) {
      this.handleError("Error binding events", error);
    }
  }

  bindNavigationEvents() {
    this.backButton?.addEventListener("click", () =>
      this.handleNavigation("back")
    );
    this.nextButton?.addEventListener("click", () =>
      this.handleNavigation("next")
    );
  }

  bindStep1Events() {
    const baseProducts = this.container.querySelectorAll(
      '[data-step="0"] .charm-builder__product-card'
    );
    baseProducts.forEach((card) => {
      // Variant Selection
      const variantSelect = card.querySelector(
        ".charm-builder__variant-select"
      );
      if (variantSelect) {
        variantSelect.addEventListener("change", (e) =>
          this.handleVariantChange(e, card)
        );
      }

      // Product Selection
      const selectButton = card.querySelector(".charm-builder__button");
      if (selectButton) {
        selectButton.addEventListener("click", () =>
          this.handleBaseSelection(card)
        );
      }
    });
  }

  bindStep2Events() {
    const addCharmButtons = this.container.querySelectorAll(
      '.charm-builder__button[data-action="add-charm"]'
    );

    if (!addCharmButtons.length) {
      this.debug("No Add Charm buttons found.");
      return;
    }

    addCharmButtons.forEach((button) => {
      button.addEventListener("click", () => {
        const productCard = button.closest(".charm-builder__product-card");
        if (!productCard) {
          this.debug("Charm product card not found for button click.");
          return;
        }

        // Extract product and variant details
        const productId = productCard.dataset.productId;
        const variantSelect = productCard.querySelector(
          ".charm-builder__variant-select"
        );
        const charmImage = productCard.querySelector(
          ".charm-builder__product-image"
        )?.src;
        const charmTitle =
          productCard.querySelector("h3")?.textContent || "Charm";
        let variantId, charmPrice;

        if (variantSelect) {
          // Handle multiple variants
          const selectedOption =
            variantSelect.options[variantSelect.selectedIndex];
          variantId = selectedOption.value;
          charmPrice = selectedOption.dataset.price;
        } else {
          // Handle single variant
          variantId = productCard.dataset.variantId;
          charmPrice = productCard.dataset.variantPrice;
        }

        // Add the charm to the base
        this.addCharmToBase(
          charmImage,
          productId,
          charmTitle,
          charmPrice,
          variantId
        );
      });
    });
  }

  bindStep3Events() {
    // Implement when adding Step 3
  }

  /**
   * ==========================================================================
   * STEP 1: BASE SELECTION
   * Add or modify base selection functionality here
   * ==========================================================================
   */
  // Update in the STEP 1: BASE SELECTION section
  handleBaseSelection(card) {
    try {
      // Debugging to verify base selection initiation
      this.debug("Processing base selection");

      // Deselect any previously selected base product
      this.container
        .querySelectorAll('[data-step="0"] .charm-builder__product-card')
        .forEach((c) => c.classList.remove("selected"));

      // Select the clicked product card
      card.classList.add("selected");

      // Extract variant details
      const variantSelect = card.querySelector(
        ".charm-builder__variant-select"
      );
      let variantId, variantPrice, variantImage;

      if (variantSelect) {
        // Multiple variants case: get selected variant details
        const selectedOption =
          variantSelect.options[variantSelect.selectedIndex];
        variantId = variantSelect.value;
        variantPrice = selectedOption.dataset.price;
        variantImage = selectedOption.dataset.image;
      } else {
        // Single variant case: use product-level data
        variantId = card.dataset.variantId;
        variantPrice = card.dataset.variantPrice;
        variantImage =
          card.dataset.variantImage ||
          card.querySelector(".charm-builder__product-image")?.src;
      }

      // Update state with selected base product details
      this.state.selectedBaseProduct = {
        productId: card.dataset.productId,
        variantId,
        price: variantPrice,
        image: variantImage,
        title: card.querySelector("h3")?.textContent || "Base Product",
      };

      // Debugging to verify updated state
      this.debug("Base product selected", this.state.selectedBaseProduct);

      // Enable the "Next" button and proceed to the next step
      this.nextButton.disabled = false;

      // Automatically navigate to Step 2 if required
      this.handleNavigation("next");
    } catch (error) {
      // Handle and log any errors during base selection
      this.handleError("Error selecting base product", error);
    }
  }

  handleVariantChange(event, card) {
    try {
      const select = event.target;
      const selectedOption = select.options[select.selectedIndex];
      const image = card.querySelector(".charm-builder__product-image");
      const price = card.querySelector(".charm-builder__price");

      const variantImage = selectedOption.dataset.image;

      // Update card image and price
      if (variantImage) {
        image.src = variantImage;
      }

      if (selectedOption.dataset.price) {
        price.textContent = selectedOption.dataset.price;
      }

      // Update state and base container if this is the selected base product
      if (card.classList.contains("selected")) {
        this.state.selectedBaseVariant = {
          id: select.value,
          price: selectedOption.dataset.price,
          image: variantImage,
        };

        // Update base container if we're on step 2
        if (this.state.currentStep === 1) {
          this.updateBaseContainer();
        }
      }

      this.debug("Variant changed", {
        productId: select.dataset.productId,
        variantId: select.value,
        image: variantImage,
      });
    } catch (error) {
      this.handleError("Error changing variant", error);
    }
  }

  /**
   * ==========================================================================
   * STEP 2: CHARM SELECTION
   * Add charm selection methods here when implementing Step 2
   * ==========================================================================
   */
  updateBaseContainer() {
    const baseContainer = this.container.querySelector(
      ".charm-builder__base-container"
    );

    if (!baseContainer) {
      this.debug("Base container not found.");
      return;
    }

    // Get the base product image from the state
    const baseImage = this.state.selectedBaseProduct?.image;

    // Clear existing content
    baseContainer.innerHTML = "";

    // Add base product image
    if (baseImage) {
      const baseImageElement = document.createElement("img");
      baseImageElement.src = baseImage;
      baseImageElement.alt =
        this.state.selectedBaseProduct?.title || "Base Product";
      baseImageElement.className = "charm-builder__base-image";
      baseContainer.appendChild(baseImageElement);
    }

    // Add charm elements from the state
    this.state.selectedCharms.forEach((charm) => {
      const charmElement = document.createElement("div");
      charmElement.className = "charm-builder__charm";
      charmElement.dataset.uniqueId = charm.uniqueId; // Include unique identifier
      charmElement.dataset.productId = charm.productId;
      charmElement.dataset.variantId = charm.variantId;
      charmElement.setAttribute("data-x", charm.x);
      charmElement.setAttribute("data-y", charm.y);
      charmElement.setAttribute("data-rotation", charm.rotation);
      charmElement.style.position = "absolute";
      charmElement.style.top = "50%";
      charmElement.style.left = "50%";
      charmElement.style.transform = `translate(${charm.x}px, ${charm.y}px) rotate(${charm.rotation}deg)`;

      charmElement.innerHTML = `
      <img src="${charm.image}" alt="${charm.title}">
      <div class="charm-builder__charm-controls">
        <button class="charm-builder__charm-control" data-action="rotate-left">⟲</button>
        <button class="charm-builder__charm-control" data-action="remove">✖</button>
        <button class="charm-builder__charm-control" data-action="rotate-right">⟳</button>
      </div>
    `;

      baseContainer.appendChild(charmElement);

      // Re-bind charm controls and dragging
      this.bindCharmControls(charmElement);
      this.enableDragForCharm(charmElement);
    });

    this.debug("Base container updated with charms.");
  }

  /**
   * ==========================================================================
   * STEP 3: REVIEW
   * Add review methods here when implementing Step 3
   * ==========================================================================
   */
  populateReviewStep() {
    const reviewContainer = this.container.querySelector(
      ".charm-builder__review"
    );
    if (!reviewContainer) {
      this.debug("Review container not found.");
      return;
    }

    const { selectedBaseProduct, selectedCharms } = this.state;

    // Helper function to parse price string into number
    const getNumericPrice = (priceString) => {
      if (!priceString) return 0;
      // Remove currency symbol and any non-numeric characters except . and ,
      const cleanPrice = priceString.replace(/[^\d.,]/g, "");
      // Remove commas and parse as float
      return parseFloat(cleanPrice.replace(/,/g, ""));
    };

    // Helper function to format price with proper currency format
    const getFormattedPrice = (price) => {
      if (!selectedBaseProduct?.price) return price;

      // Get currency symbol
      const currencySymbol = selectedBaseProduct.price
        .replace(/[0-9.,]/g, "")
        .trim();

      // Format number with thousand separator and 2 decimal places
      const formattedNumber = price
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

      return `${currencySymbol}${formattedNumber}`;
    };

    // Calculate total price
    const basePrice = getNumericPrice(selectedBaseProduct?.price) || 0;
    const charmsPrice = selectedCharms.reduce((total, charm) => {
      const charmPrice = getNumericPrice(charm.price) || 0;
      return total + charmPrice;
    }, 0);
    const totalPrice = basePrice + charmsPrice;

    // Base Product Review
    const baseHtml = selectedBaseProduct
      ? `
    <div class="charm-builder__selected-item">
      <img src="${selectedBaseProduct.image}" alt="${selectedBaseProduct.title}">
      <div class="charm-builder__item-details">
        <h4>${selectedBaseProduct.title}</h4>
        <p>${selectedBaseProduct.price}</p>
      </div>
    </div>
  `
      : "<p>No base jewelry selected.</p>";

    // Charm Review
    const charmsHtml =
      selectedCharms.length > 0
        ? selectedCharms
            .map(
              (charm) => `
    <div class="charm-builder__selected-item">
      <img src="${charm.image}" alt="${charm.title}">
      <div class="charm-builder__item-details">
        <h4>${charm.title}</h4>
        <p>${charm.price}</p>
      </div>
    </div>
  `
            )
            .join("")
        : "<p>No charms selected.</p>";

    // Populate review content
    reviewContainer.innerHTML = `
    <div class="charm-builder__selection-summary">
      <h4>Base Jewelry</h4>
      ${baseHtml}
      <h4>Charms</h4>
      ${charmsHtml}
    </div>
    <div class="charm-builder__total">
      <h4>Total Price:</h4>
      <p>${getFormattedPrice(totalPrice)}</p>
    </div>
  `;
  }

  bindCartDrawerEvents() {
    const cartDrawer = document.querySelector("cart-drawer");
    const closeButton = cartDrawer?.querySelector("[data-cart-close]");

    // Close drawer on click
    closeButton?.addEventListener("click", () => {
      cartDrawer.classList.remove("active");
    });

    this.debug("Cart drawer events bound.");
  }

  updateCartDrawer() {
    fetch("/cart.js", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((cart) => {
        const cartDrawer = document.querySelector("cart-drawer");
        const cartIcon = document.getElementById("cart-icon-bubble");
        let cartCountBubble = cartIcon.querySelector(".cart-count-bubble");

        if (!cartDrawer) {
          console.error("Cart drawer element not found.");
          return;
        }

        // Ensure the cart has products
        if (!cart.items || cart.items.length === 0) {
          console.error("Cart is empty. No products to display.");
          if (cartCountBubble) {
            cartCountBubble.textContent = "0";
            cartCountBubble.classList.add("hidden");
          }
          return;
        }

        // Create cart-count-bubble if it doesn't exist
        if (!cartCountBubble) {
          cartCountBubble = document.createElement("div");
          cartCountBubble.className = "cart-count-bubble";
          cartCountBubble.innerHTML = `
          <span aria-hidden="true">${cart.item_count}</span>
          <span class="visually-hidden">${cart.item_count} item${
            cart.item_count > 1 ? "s" : ""
          }</span>
        `;
          cartIcon.appendChild(cartCountBubble);
        } else {
          // Update existing cart-count-bubble
          cartCountBubble.innerHTML = `
          <span aria-hidden="true">${cart.item_count}</span>
          <span class="visually-hidden">${cart.item_count} item${
            cart.item_count > 1 ? "s" : ""
          }</span>
        `;
          cartCountBubble.classList.remove("hidden");
        }

        // Fetch updated cart drawer HTML
        fetch("/cart?section_id=cart-drawer", { method: "GET" })
          .then((res) => res.text())
          .then((html) => {
            const parser = new DOMParser();
            const newDocument = parser.parseFromString(html, "text/html");

            // Replace cart drawer contents
            const drawerContent = newDocument.querySelector("cart-drawer");
            if (drawerContent) {
              cartDrawer.innerHTML = drawerContent.innerHTML;
              cartDrawer.classList.add("active"); // Show cart drawer
              cartDrawer.classList.remove("is-empty"); // Ensure it is not marked empty
              this.bindCartDrawerEvents(); // Rebind drawer events if necessary
            } else {
              console.error("Updated cart drawer content not found.");
            }
          });
      })
      .catch((error) => {
        console.error("Error updating cart drawer:", error);
      });
  }

  handleAddToCart() {
    const { selectedBaseProduct, selectedCharms } = this.state;

    if (!selectedBaseProduct) {
      alert("Please select a base product before adding to the cart.");
      return;
    }

    // Build cart items
    const cartItems = [
      {
        id: selectedBaseProduct.variantId,
        quantity: 1,
      },
      ...selectedCharms.map((charm) => ({
        id: charm.variantId,
        quantity: 1,
      })),
    ];

    // Debug cart items
    this.debug("Adding items to cart", cartItems);

    // Add items sequentially to the cart to avoid conflicts
    const addItemsToCart = async () => {
      try {
        for (const item of cartItems) {
          const response = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(item),
          });

          if (!response.ok) {
            throw new Error(`Failed to add item: ${JSON.stringify(item)}`);
          }

          this.debug(`Item added to cart: ${JSON.stringify(item)}`);
        }

        this.updateCartDrawer(); // Refresh cart drawer
      } catch (error) {
        console.error("Error adding items to cart:", error);
        alert("There was an error adding items to the cart. Please try again.");
      }
    };

    addItemsToCart();
  }

  /**
   * ==========================================================================
   * NAVIGATION & UI UPDATES
   * Update these methods when adding new steps or UI features
   * ==========================================================================
   */
  handleNavigation(direction) {
    try {
      const currentStep = this.state.currentStep;
      const nextStep = direction === "next" ? currentStep + 1 : currentStep - 1;

      if (nextStep >= 0 && nextStep <= 2 && this.canNavigate(nextStep)) {
        this.state.currentStep = nextStep;
        this.updateUI();
        this.debug("Navigation successful", {
          from: currentStep,
          to: nextStep,
        });
      }
    } catch (error) {
      this.handleError("Navigation error", error);
    }
  }
  handleNavigation(direction) {
    try {
      const currentStep = this.state.currentStep;
      const nextStep = direction === "next" ? currentStep + 1 : currentStep - 1;

      if (direction === "next" && currentStep === 2) {
        // Call handleAddToCart on Step 3 when "Next" is clicked
        this.handleAddToCart();
        return; // Exit the method as we're handling the cart action
      }

      if (nextStep >= 0 && nextStep <= 2 && this.canNavigate(nextStep)) {
        this.state.currentStep = nextStep;
        this.updateUI();
        this.debug("Navigation successful", {
          from: currentStep,
          to: nextStep,
        });
      }
    } catch (error) {
      this.handleError("Navigation error", error);
    }
  }

  canNavigate(toStep) {
    if (toStep > this.state.currentStep) {
      // Add validation for each step
      switch (this.state.currentStep) {
        case 0:
          return !!this.state.selectedBaseProduct;
        case 1:
          return true; // Update when implementing Step 2
        case 2:
          return true; // Update when implementing Step 3
        default:
          return true;
      }
    }
    return true; // Always allow backward navigation
  }

  updateUI() {
    try {
      // Update step visibility
      this.steps.forEach((step, index) => {
        step.classList.toggle(
          "charm-builder__hidden",
          index !== this.state.currentStep
        );
      });

      // Update progress bar
      this.updateProgressBar();

      // Update navigation
      this.updateNavigation();

      // Handle base image when entering Step 2
      if (this.state.currentStep === 1) {
        this.debug("Updating base container image");
        this.updateBaseContainer();
      }

      // Populate review UI on Step 3
      if (this.state.currentStep === 2) {
        this.populateReviewStep();
      }

      this.debug("UI updated", { currentStep: this.state.currentStep });
    } catch (error) {
      this.handleError("Error updating UI", error);
    }
  }

  updateProgressBar() {
    this.progressBar.setAttribute("data-step", this.state.currentStep);

    const steps = this.progressBar.querySelectorAll(
      ".charm-builder__progress-step"
    );
    steps.forEach((step, index) => {
      step.classList.remove("active", "completed");
      if (index === this.state.currentStep) {
        step.classList.add("active");
      } else if (index < this.state.currentStep) {
        step.classList.add("completed");
      }
    });

    // Change progress bar gradient based on step completion
    if (this.state.currentStep > 0) {
      this.progressBar.classList.add("completed");
    } else {
      this.progressBar.classList.remove("completed");
    }
  }

  updateNavigation() {
    this.backButton.disabled = this.state.currentStep === 0;
    this.nextButton.disabled = !this.canNavigate(this.state.currentStep + 1);
    this.nextButton.textContent =
      this.state.currentStep === 2 ? "Add to Cart" : "Next";
  }

  /**
   * ==========================================================================
   * UTILITY METHODS
   * Add new utility methods here
   * ==========================================================================
   */
  debug(message, data = null) {
    if (!this.debugContainer) return;

    const timestamp = new Date().toLocaleTimeString();
    const debugMessage = data
      ? `[${timestamp}] ${message}: ${JSON.stringify(data)}`
      : `[${timestamp}] ${message}`;

    this.debugContainer.innerHTML = `
      ${debugMessage}<br>${this.debugContainer.innerHTML}
    `.slice(0, 1000);

    this.debugContainer.style.display = "block";
    setTimeout(() => {
      this.debugContainer.style.display = "none";
    }, 5000);
  }

  handleError(message, error) {
    this.debug(`ERROR: ${message}`, error.message);
    this.state.lastError = error.message;
  }

  /**
   * ==========================================================================
   * STATIC INITIALIZATION
   * Update if initialization process needs to change
   * ==========================================================================
   */
  static init() {
    document.querySelectorAll('[id^="charm-builder-"]').forEach((container) => {
      new CharmBuilder(container.id);
    });
  }
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", CharmBuilder.init);
} else {
  CharmBuilder.init();
}
